import { useState, useEffect } from 'react';
import moment from 'moment';
import { fLMS, mLMS } from './bmiLMFacts';
import { Company } from './enums';

import { ErrorResponse } from 'utils/types';
import notifier from 'utils/notifier';
import { ORGANIZATION_NAME } from './objects';

import { Modal } from 'antd';
import constants from 'constants/index';
import { ALL_TEXT } from 'utils/objects';
import { AddAppInsightEvent } from 'index';

export const useDebounce = <T>(value: T, delay: number): T => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

export const toLetters = (num: number): string => {
  let mod = num % 26,
      pow = num / 26 | 0,
      out = mod ? String.fromCharCode(64 + mod) : (--pow, 'Z');
  return pow ? toLetters(pow) + out : out;
};

const sortArrayByKey = (arrayName: any, key: string) => {
  return arrayName.sort((elementA: any, elementB: any) => {
    let objectA = elementA[key].toLowerCase();
    let objectB = elementB[key].toLowerCase();
    if (objectA < objectB){
      return -1;
    }
    if (objectA > objectB){
      return 1;
    }
    return 0;
  });
}

const sortArrayByKeyNumber = (arrayName: any, key: string, order: string) => {
  return arrayName.sort((elementA: any, elementB: any) => {
    let objectA = elementA[key];
    let objectB = elementB[key];
    if (order === 'desc') return objectB - objectA;
    if (order === 'asc') return objectA - objectB;
    return 0;
  });
}

const sortByIPA = (arrayName: any, key: string) => {
  const splitByIPA = sortArrayByKey(arrayName, key).reduce((memo: any, x: any) => {
    if (!memo[x['RmiCompanyId']]) { memo[x['RmiCompanyId']] = []; }
    memo[x['RmiCompanyId']].push(x);
    return memo;
  }, {} as Object);

  const result = splitByIPA[sessionStorage.getItem('ipa')!] ? [...splitByIPA[sessionStorage.getItem('ipa')!]] : [];

  Object.keys(splitByIPA).forEach((ipa: any) => {
    if (ipa !== sessionStorage.getItem('ipa')) {
      result.push(...splitByIPA[ipa]);
    }
  });

  /** Replace CVIPA with EHIPA */
  result.forEach((data: any) => {
    if (data.RmiCompanyId === Company.CVIPA) {
      data.Provider = data.Provider?.replace(Company.CVIPA, Company.EHIPA);
    }
  });

  return result;
}

const formatPhoneNumber = (phoneNumber: string) => {
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = (match[1] ? '+1 ' : '');
    return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
  }
  return '';
}

// must pass in input event
const numberInputOnly = (input: any) => {
  if (input.which < 48 || input.which > 57) input.preventDefault();

  const output = input.target.value;
  return output;
}

const validateNumberInputOnly = (input: any) => {
  if (!input?.match(/^\d+$/)) {
    return Promise.reject(new Error('Please enter digits only.'))
  }
  return Promise.resolve();
}

const validateNumberExtension = (input: any) => {
  if (input && !input.match(/^\d+$/)) {
    return Promise.reject(new Error('Please enter digits only.'))
  }
  return Promise.resolve();
}

// must pass in input event
const validateZipCode = (input: any) => {
  if (input && (!input?.match(/^\d+$/) || input?.length != 5)) {
    return Promise.reject(new Error('Please enter valid zipcode.'))
  }
  return Promise.resolve();
}

// must pass in input event
const validateLetterOnly = (input: any) => {
  if (!input?.match(/^[a-zA-Z]+$/)) {
    return Promise.reject(new Error('Please enter letters only.'))
  }
  return Promise.resolve();
}

// returns (###) ###-#### when typing and only allows digits
const phoneFormatInput = (input: any) => {

  numberInputOnly(input);
  if (input.target.value.length > 13) input.preventDefault();

  let output = "";
  output = input.target.value.replace(/\D/g,'');
  var size = output.length;
  if (size > 13) input.preventDefault();
  if (size > 0) { output= "(" + output }
  if (size > 3) { output = output.slice(0,4) + ") " + output.slice(4,11) }
  if (size > 6) { output = output.slice(0, 9) + "-" + output.slice(9) }
  return output;
}

//formats 9 digit zip code
const formatZipCode = (zipCode: string) => {
  if (!zipCode || zipCode === 'undefined')
    return '';
  const regex = /(\d{5})(\d{4})/;
  if (zipCode.length !== 5 && zipCode.length !== 9)
    return 'Invalid ZIP Code';
  if (zipCode.length === 5)
    return zipCode;
  if (zipCode.length === 9)
    return zipCode.replace(regex, "$1-$2");
  return '';
}

//capitalizes first letter of each word in a string separated by spaces
const capitalizeFirstLetter = (text: string) => {
  return text.toLowerCase()
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.substring(1))
    .join(' ');
}

const groupArrayOfObjectsByKey = (array: any, key: any) => {
  return array.reduce((previousValue: any, currentValue: any) => {
    (previousValue[currentValue[key]] = previousValue[currentValue[key]] || []).push(currentValue);
    return previousValue;
  }, {});
};

//format date by checking if date is not null instead of using long ternary operation on some pages
const formatDate = (date: string | null, format: string) => {
  if (!date) return ''; //check if date is not null before passing to moment
  return moment(date).format(format);
};

const sortResources = (data: any[], languages: any[]) => {
  let j = 0, i = 0, n = 0;
  languages[j] = [];
  do {
    let fileDesc = languages[j].length > 0 ? languages[j][n].RmiFileDesc : null;
    if (languages[j].length === 0) {
      languages[j].push(data[i]);
    } else if (data[i].RmiFileDesc === fileDesc) {
      let containsLanguage = false;
      for (let k = 0; k < languages[j].length; k++) {
        if (languages[j][k].RmiLanguage === data[i].RmiLanguage) {
          containsLanguage = true;
        }
      }
      if (containsLanguage === false) {
        languages[j].push(data[i]);
      }
    } else {
      j++;
      languages[j] = [];
      languages[j].push(data[i]);
    }
    i++;
  } while (i < data.length);
};

const getEnumKeyByEnumValue = (enName: any, enumValue: any) => {
  let keys = Object.keys(enName).filter(x => enName[x] === enumValue);
  return keys.length > 0 ? keys[0] : null;
};

const getMimeType = (fileName: string) => {

  let extension = "." + fileName.split(".").pop()?.toUpperCase();
  let mimeType = "";

  switch (extension) {
    case ".DOC":
      mimeType = "application/msword";
      break;
    case ".DOCX":
      mimeType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      break;
    case ".XLS":
      mimeType = "application/vnd.ms-excel";
      break;
    case ".XLSX":
      mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      break;
    case ".CSV":
      mimeType = "text/csv";
      break;
    case ".PDF":
      mimeType = "application/pdf";
      break;
    case ".TIF":
      mimeType = 'image/tiff';
      break;
    case ".TIFF":
      mimeType = 'image/tiff';
      break;
    case "JPG":
      mimeType = 'image/jpeg';
      break;
    case "JPEG":
      mimeType = 'image/jpeg';
      break;
    case ".PPTX":
      mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      break;
    default:
      mimeType = 'application/' + extension;
  }
  return mimeType;
};

const calcAgeInYrsMo = (birthday: string) => {
  if (birthday) {
    const years = moment().diff(birthday, 'years');
    const totalMonths = moment().diff(birthday, 'months');
    const months = totalMonths % 12;

    return years + ' years ' + months + (months > 0 ? ' months' : ' month');
  }
  return null;
}

const calcAgeInMonths = (birthday: string) => {
  if(birthday) return moment().diff(birthday, 'months');
  return 0;
}

const convertHeight = (heightInches: number) => {
  const height = {
      heightFeet: 0,
      heightInches: heightInches % 12
  };

  height.heightFeet = (heightInches - height.heightInches) / 12;

  return height;
}

const calculateHeightInInches = (bmi: any) => {
  const feet = (parseInt(bmi.heightFeetVitals) * 12);
  const heightInches = feet + parseFloat(bmi.heightInchesVitals);
  return heightInches;
}

//legacy ported code to compute bmiPercentile
const computeBmiPercentile = (age: number, bmi: any, sex: string) => {
  let bmiLMS = new Array(3);
  if (sex === "F") {
      //female
      bmiLMS = fLMS(age);
  } else {
      //male
      bmiLMS = mLMS(age);
  }
  let L = bmiLMS[0];
  let M = bmiLMS[1];
  let S = bmiLMS[2];
  let z = (Math.pow((bmi / M), L) - 1) / (L * S);
  let pP, pcnt;
  //
  pP = 1 - 1 / Math.sqrt(2 * 3.14159265) * Math.exp(-(Math.pow(Math.abs(z), 2)) / 2) * (0.4361836 * (1 / (1 + 0.33267 * Math.abs(z))) - 0.1201676 * Math.pow((1 / (1 + 0.33267 * Math.abs(z))), 2) + 0.937298 * Math.pow((1 / (1 + 0.33267 * Math.abs(z))), 3));
  //
  if (z > 0) {
      pcnt = pP * 100;
  } else {
      pcnt = 100 - (pP * 100);
  }
  z = Math.round(z * 100) / 100;
  pcnt = Math.round(pcnt);
  return pcnt;
}

//legacy ported code to compute computeBMI
const computeBMI = (wt: number, ht: number, meas: number) => {
  let htlocal = ht;
  let wtlocal = wt;
  if (meas === 1) {
      //english units - convert to kg,cm
      wtlocal /= 2.2;
      htlocal *= 2.54;
  }
  let bmi = wtlocal / ((htlocal / 100) * (htlocal / 100));
  return Math.round(bmi * 100) / 100;
}

const getBlobTypeFromFileExtension = (ext: string | undefined) => {
  switch(ext) {
    case "docx":
      return "vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "xlsx":
      return 'vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case "xls":
      return "vnd.ms-excel";
    case "pdf":
      return "pdf";
    default:
      return ext;
  }
}

const handleDownload = (payload: any, filename: string) => {
  let ext = getBlobTypeFromFileExtension(filename.split('.').pop()?.toLowerCase());
  let blob = new Blob([payload], {type: "application/" + ext});
  const fileURL = URL.createObjectURL(blob);
  if (ext !== 'pdf') {
    let a = document.createElement('a');
    a.href = fileURL;
    a.download = filename;
    a.click();
  } else {
    window.open(fileURL, "_blank", "");
  }
}

//If there is exactly one match, then we will default to the provider. Otherwise, we are going to let the provider choose or default to all providers
const selectDefaultProvider = (array: any[], defaultValue: any, type?: string) => {

  const defaultProvider = array.filter((provider: any) => provider.RmiCompanyId === sessionStorage.getItem('ipaGroup'));

  if (defaultProvider.length !== 1) {
    return defaultValue;
  }

  else if (type === 'string') {
    return JSON.stringify(defaultProvider[0]);
  }

  else {
    return defaultProvider[0];
  }
}

const getPortalName = () => {
  return sessionStorage.getItem('ipaGroup') !== 'APC' ? sessionStorage.getItem('ipaGroup') || 'NMM' : 'NMM';
}

const setPortalName = (portalName: string) => {
  sessionStorage.setItem('ipaGroup', portalName);
}

const getDisplayCompanyId = (companyId: string) => {
  if (companyId === Company.CVIPA) {
    companyId =  Company.EHIPA;
  }
  return companyId;
}

const groupByCptCodes = (arrayOfObjects: any[]) => {

  return arrayOfObjects.reduce((result: any, current: any) => {

    let key = current['RmiProcCode'];
    let entry = result[key];

    if (entry && current['RmiModif'] !== '') {
      entry.push(current);
    }

    else if (current['RmiModif'] === '') {
      result[key + '_'] = [current];
    }

    else {
      result[key] = [current];
    }

    return result;
  }, {});
}

const getFileExtension = (fileName: string) => {
  return fileName?.substr(fileName.lastIndexOf('.') + 1);
};

const showGenericError = (err: ErrorResponse) => {
  notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
};

const showAuthSubmissionBlockingModalForAutoPaidClaims = () => {
  Modal.warning({
    title: "Claim Auto-Paid",
    content: `No authorization number is required for this service. Kindly submit your claim directly to ${ORGANIZATION_NAME} for payment.`,
  });
};

const validateEmailAddress = async (_: any, value: any) => {
  if (value && !value.match(/\S+@\S+\.\S+/)) {
    return Promise.reject(new Error('Please enter a valid email address.'))
  }
  return Promise.resolve();
};

const isEmailAddressValid = (value: any) => {
  if (!value) {
    return false;
  }
  if (value && !value.match(/\S+@\S+\.\S+/)) {
    return false;
  }
  return true;
};

// length is less than 14 is because for the phone number formatting
const validatePhoneNumber = async (_: any, value: any) => {
  if (value && value.length < 14) {
    return Promise.reject(new Error('Please enter a valid phone number.'))
  }
  return Promise.resolve();
};

const validateCharges = (input: any) => {
  let decimalPos = input.target.value.indexOf('.');
  if (decimalPos > 0 ) {
    let length = input.target.value.length;
    if (length - decimalPos > 2) return false;
  }
  return true;
};

const isUserEnabledForPBI = (userId: any, enabledUsers: any) => {
  const enabledUserIds = enabledUsers.split(',').map((user: any) => user.trim())
  return (enabledUserIds.includes(userId) || enabledUserIds.includes(ALL_TEXT)) &&
         (constants.PBI_ENABLE_IPA.includes(ALL_TEXT) || constants.PBI_ENABLE_IPA.includes(sessionStorage.getItem("ipa")));
};

const addCustomAppInsightsEvent = (eventName: string, payload: any) => {
  AddAppInsightEvent(
    {
      "name": eventName,
      "properties": {
        "payload": payload
      },
    },
    sessionStorage.user_id
  );
};

export {
  sortArrayByKey,
  sortArrayByKeyNumber,
  sortByIPA,
  formatPhoneNumber,
  phoneFormatInput,
  numberInputOnly,
  formatZipCode,
  capitalizeFirstLetter,
  groupArrayOfObjectsByKey,
  formatDate,
  sortResources,
  getEnumKeyByEnumValue,
  calcAgeInYrsMo,
  calcAgeInMonths,
  convertHeight,
  getMimeType,
  calculateHeightInInches,
  computeBmiPercentile,
  computeBMI,
  getBlobTypeFromFileExtension,
  handleDownload,
  selectDefaultProvider,
  getPortalName,
  getDisplayCompanyId,
  groupByCptCodes,
  getFileExtension,
  showGenericError,
  showAuthSubmissionBlockingModalForAutoPaidClaims,

  validateEmailAddress,
  validatePhoneNumber, validateCharges,
  isEmailAddressValid,
  validateLetterOnly,
  validateZipCode,
  validateNumberInputOnly,
  validateNumberExtension,
  isUserEnabledForPBI,
  addCustomAppInsightsEvent
};

